/**
 * Утилиты связанные с маршрутизацией
 * @module
 */

import type { IndexRouteObject, LazyRouteFunction, RouteObject } from "react-router-dom-7"

const missingDynamicRouterPluginError = () => {
	throw new Error("Подключите dynamicRouterPlugin в vite.config.mts")
}

export const route = (path: string, lazyOrPath: LazyRouteFunction<RouteObject> | string, children: RouteObject[] | undefined = undefined): RouteObject => typeof lazyOrPath === "string"
	? {
		path,
		element: null,
		loader: missingDynamicRouterPluginError
	}
	: {
		path,
		lazy: lazyOrPath,
		children
	}

export const index = (lazyOrPath: LazyRouteFunction<RouteObject> | string): IndexRouteObject => typeof lazyOrPath === "string"
	? {
		index: true,
		element: null,
		loader: missingDynamicRouterPluginError
	}
	: {
		index: true,
		lazy: lazyOrPath
	}

export const layout = (lazyOrPath: LazyRouteFunction<RouteObject> | string, children: RouteObject[] | undefined = undefined): RouteObject => {

	if (typeof lazyOrPath === "string") {
		return {
			element: null,
			loader: missingDynamicRouterPluginError
		} satisfies RouteObject
	}

	return {
		lazy: lazyOrPath,
		children
	}
}

/**
 * Разделитель сегментов пути в именах папок — `~` (например `edit~{auditoryId}~computers`).
 * Двойная `~~` — экранирование: даёт литеральную тильду в сегменте.
 * Также `{param}` → `:param`.
 */
function decodeRoutePath(rawPath: string): string {
	return rawPath
		.replace(/~~|~/g, (match) => (match === "~~" ? "~" : "/"))
		.replace(/\{([^/]+)\}/g, ":$1")
}

function convertModulePathToRoutePath(modulePath: string) {

	if (modulePath === "index") return null

	return decodeRoutePath(modulePath)
}



/**
 * Преобразует список модулей в маршруты.
 * @param modules список модулей. Формируется с помощью `import.meta.glob`
 * @param parseFn функция, которая должна преобразовать путь модуля в путь маршрута
 *
 * @example
 *
 * import { globToRoutes } from "~/shared/lib/routing"
 *
 * function parseModulePath(modulePath : string) {
 *     /\* ...Код парсинга... \*\/
 * }
 *
 * export const routes = globToRoutes(import.meta.glob(["./routes/\*\/index.tsx"]), parseModulePath)
 *
 */
export function globToRoutes(modules: Record<string, () => Promise<unknown>>, parseFn : (modulePath : string) => string | null ) {
	const entries = Object.entries(modules as Record<string, LazyRouteFunction<RouteObject>>)
	const routes: RouteObject[] = []

	for (const entry of entries) {
		const parsed = parseFn(entry[0])

		if (!parsed) throw new Error("Parsing error for module " + entry[0])

		const path = convertModulePathToRoutePath(parsed)

		if (path) routes.push({ path, lazy: entry[1] })
		else routes.push({ index: true, lazy: entry[1] })
	}

	return routes
}

function parseModulePath(moduleName : string, regExp : RegExp) {
	const parsed = regExp.exec(moduleName)

	if(!parsed || parsed.length < 2) return null

	return parsed[1]
}

const flatRoutesRegExp = /^\.\/routes\/([^/]+)\/index\.(?:tsx?|jsx?)$/
const treeRoutesRegExp = /^\.\/routes\/(.+)\/(route|layout)\.(?:tsx?|jsx?)$/

/**
 * Преобразует плоский список модулей в маршруты. Модули должны располагаться в папке `routes`.
 * Каждый модуль в отдельной папке с файлом `index.{ts,tsx,js,jsx}`.
 * Путь маршрута определяется из пути модуля, относительно папки `routes`, и не включает имя файла `index.{ts,tsx,js,jsx}`.
 * @param modules список модулей. Формируется с помощью `import.meta.glob(["./routes/*\/index.tsx"])`
 *
 * @example
 *
 * import { globToFlatRoutes } from "~/shared/lib/routing"
 *
 * export const routes = globToFlatRoutes(import.meta.glob(["./routes/*\/index.tsx"]))
 *
 */
export function globToFlatRoutes(modules: Record<string, () => Promise<unknown>>){
	return globToRoutes(modules, modulePath => parseModulePath(modulePath, flatRoutesRegExp))
}

/**
 * Преобразует путь модуля дерева в путь маршрута.
 * В tree-режиме иерархия выражается ТОЛЬКО реальными вложенными папками: имя папки —
 * литеральный сегмент пути, поэтому `~` здесь НЕ является разделителем (это разделитель
 * плоского формата, см. {@link decodeRoutePath}). Из имени конвертируется лишь `{param}` → `:param`.
 * В отличие от {@link convertModulePathToRoutePath}, `index` не схлопывается в корневой index-роут:
 * он остаётся сегментом и обрабатывается билдером как index-ребёнок своего родителя.
 */
function convertTreeModulePath(rawPath: string): string {
	return rawPath.replace(/\{([^/]+)\}/g, ":$1")
}

type FolderNode = {
	layoutLazy?: LazyRouteFunction<RouteObject>
	routeLazy?: LazyRouteFunction<RouteObject>
	children: Map<string, FolderNode>
}

function ensureFolder(root: Map<string, FolderNode>, segments: string[]): FolderNode {
	let map = root
	let node!: FolderNode

	for (const segment of segments) {
		let child = map.get(segment)
		if (!child) {
			child = { children: new Map<string, FolderNode>() }
			map.set(segment, child)
		}
		node = child
		map = child.children
	}

	return node
}

/**
 * Рекурсивно превращает папку в список дочерних маршрутов.
 * - Папка с `layout.tsx` — layout-нода (`Component` рендерит `<Outlet/>`), дети собираются автоматически.
 * - Папка с `route.tsx` и дочерними папками, но без `layout.tsx` — группирующая path-нода
 *   без компонента (React Router рендерит `<Outlet/>` по умолчанию), её `route.tsx` становится index-ребёнком.
 * - Папка `index` с `route.tsx` — index-ребёнок родителя.
 * - Лист с `route.tsx` — обычная страница.
 */
function emitFolderChildren(map: Map<string, FolderNode>): RouteObject[] {
	const routes: RouteObject[] = []

	for (const [segment, node] of map) {
		const childRoutes = emitFolderChildren(node.children)
		const isIndex = segment === "index"

		if (node.layoutLazy) {
			const children: RouteObject[] = []
			if (node.routeLazy) children.push({ index: true, lazy: node.routeLazy })
			children.push(...childRoutes)

			routes.push(isIndex
				? { lazy: node.layoutLazy, children }
				: { path: segment, lazy: node.layoutLazy, children })
			continue
		}

		if (childRoutes.length > 0) {
			const children: RouteObject[] = []
			if (node.routeLazy) children.push({ index: true, lazy: node.routeLazy })
			children.push(...childRoutes)

			routes.push(isIndex
				? { children }
				: { path: segment, children })
			continue
		}

		if (node.routeLazy) {
			routes.push(isIndex
				? { index: true, lazy: node.routeLazy }
				: { path: segment, lazy: node.routeLazy })
		}
	}

	return routes
}

/**
 * Преобразует иерархический список модулей в маршруты. Модули располагаются в папке `routes`.
 * Роль модуля определяется по имени файла:
 * - `route.{ts,tsx,js,jsx}` — страница (лист);
 * - `layout.{ts,tsx,js,jsx}` — страница-макет: её `Component` рендерит `<Outlet/>`,
 *   а дочерние страницы вложенных папок собираются в `children` автоматически.
 * Пути собираются статически из ключей glob (компоненты остаются ленивыми), относительные пути детей
 * корректно склеиваются React Router. Папки без `layout` дают плоскую логическую иерархию через имена папок.
 * @param modules список модулей. Формируется с помощью
 * `import.meta.glob(["./routes/**\/*\/route.tsx", "./routes/**\/*\/layout.tsx"])`
 *
 * @example
 *
 * import { globToTreeRoutes } from "~/shared/lib/routing"
 *
 * export const routes = globToTreeRoutes(
 *   import.meta.glob(["./routes/**\/*\/route.tsx", "./routes/**\/*\/layout.tsx"])
 * )
 */
export function globToTreeRoutes(modules: Record<string, () => Promise<unknown>>): RouteObject[] {
	const root = new Map<string, FolderNode>()

	for (const [key, lazy] of Object.entries(modules)) {
		const matched = treeRoutesRegExp.exec(key)
		if (!matched || matched.length < 3) continue

		const [, rawPath, kind] = matched
		const converted = convertTreeModulePath(rawPath)
		const node = ensureFolder(root, converted.split("/"))

		if (kind === "layout") node.layoutLazy = lazy as LazyRouteFunction<RouteObject>
		else node.routeLazy = lazy as LazyRouteFunction<RouteObject>
	}

	return emitFolderChildren(root)
}